import { createContext, useContext } from "react";
import _ from "lodash";

import { CompetitorClaim } from "./external";
import { useCrud } from "../BackendProvider";
import { competitor_claim } from "../data";
import { useExternal } from "./";

export type AggregatedCompetitorClaims = Record<number, {
    claimsByMemberId: Record<number, CompetitorClaimVersion[]>,
}>

export interface CompetitorClaimVersion {
    version: string
    versionDate: string
    claims: CompetitorClaim[]
}

const ExternalClaimsContext = createContext({
    claims: [] as CompetitorClaim[],
    postClaim: (claim: CompetitorClaim) => Promise.resolve({}),
    deleteClaim: (claim: CompetitorClaim) => Promise.resolve({}),

    aggregatedClaims: {} as AggregatedCompetitorClaims,

    isLoading: false,
    reload: () => {},
})

export function useExternalClaims() {
    return useContext(ExternalClaimsContext)
}

export function ExternalClaimsProvider({children}) {
    const {members} = useExternal()

    const {data: claims, postMutation: postClaim, deleteMutation: deleteClaim, isLoading, reload} =
        useCrud<CompetitorClaim>(competitor_claim, c => c.competitorClaimId)

    const familyByMemberId: Record<number, number> = _(members).map(m => [m.competitorMemberId, m.competitorFamilyId]).fromPairs().value()

    const aggregatedClaims: AggregatedCompetitorClaims = _(claims)
        .groupBy(c => familyByMemberId[c.competitorMemberId])
        .mapValues(claims => {
            const claimsByMemberId = _(claims)
                .groupBy(c => c.competitorMemberId)
                .mapValues(cs => _(cs)
                    .groupBy(c => c.version)
                    .map(byVersion => ({version: byVersion[0].version, versionDate: byVersion[0].versionDate, claims: _.sortBy(byVersion, c => c.claimNumber)}))
                    .sortBy('versionDate')
                    .value())
                .value()

            return {claimsByMemberId}
        })
        .value()

    const value = {
        claims, postClaim, deleteClaim,
        aggregatedClaims,
        isLoading, reload,
    }

    return <ExternalClaimsContext.Provider {...{value}}>
        {children}
    </ExternalClaimsContext.Provider>
}
